import React, { useState, useEffect, useRef } from "react";
import { Crown, Loader, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  addToTrustedList,
  getAnomalyTrustedList,
  deleteEntry,
  getAnomalyGlobalTrustedList,
} from "@services/api/amlAnomalyDetection";
import { toast } from "@components/ui/use-toast";
import DataTable from "@components/common/DataTable";
import { Checkbox } from "@components/ui/checkbox";
import TableLoader from "@components/loaders/TableLoader";
import { useMutation, useQuery } from "react-query";
import { isValidSubnetMask } from "@utils/utils";
import CustomSelect from "@components/common/CustomSelect";
import { fetchDataListData } from "@services/api/aiPoweredEngine";

interface AMLDropdownmenuProps {
  show: boolean;
  onClose: () => void;
  data?: any;
  permission?: any;
}

const TrustedListDialog: React.FC<AMLDropdownmenuProps> = ({
  show,
  onClose,
  data,
  permission,
}) => {
  const [matchedTrustedItems, setMatchedTrustedItems] = useState<any>([]);
  const [operatorType, setoperatorType] = useState("AND");
  const [trustedLoader, settrustedLoader] = useState(false);
  const [trustedTableRows, settrustedTableRows] = useState([]);
  const fixAllAnomaliesRef = useRef<HTMLButtonElement>(null);
  const [untrustedRecord, setUntrustedRecord] = useState<any>([]);
  const [eventList, setEventList] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedEvent, setSelectedEvent] = useState(
    (data && data["eventNames"] && Object.keys(data["eventNames"])[0]) || "",
  );
  const [permissions, setPermissions] = useState<{
    global_trusted_list_edit: number;
    global_trusted_list_view: number;
  }>({
    global_trusted_list_edit: 0,
    global_trusted_list_view: 0,
  });
  const [activeTab, setActiveTab] = useState("untrusted");
  const expiryDateRef = useRef<HTMLInputElement>(null);
  const [inListParameters, setInListParameters] = useState<any>({});

  function showInputPicker(e: any) {
    e.target.showPicker();
  }

  const {
    data: trustedData,
    isLoading: _trustedLoading,
    refetch: trustedRefetch,
    isRefetching: trustedRefetching,
  } = useQuery(["getAnomalyTrustedList", data.anomaly_id], ({ signal }) =>
    getAnomalyTrustedList(data.anomaly_id, signal),
  );

  const { data: gTrustedList, refetch: refetchGlobalTrustedList } = useQuery(
    [
      "getAnomalyGlobalTrustedList",
      data?.anomaly_id,
      data?.globalID,
      data?.anomaly_type,
      data?.time_seen,
    ],
    ({ signal }) =>
      data
        ? getAnomalyGlobalTrustedList(
            data.anomaly_id,
            data.globalID,
            data.anomaly_type,
            data.time_seen,
            signal,
          )
        : Promise.resolve([]),
    {
      enabled: !!data,
    },
  );

  useEffect(() => {
    const tmpList: { label: string; value: string }[] = [];
    if (data && data["eventNames"]) {
      Object.entries(data["eventNames"]).map(([key, val]) => {
        const tmp = { label: val as string, value: key };
        tmpList.push(tmp);
      });
      if (tmpList.length > 0) setEventList(tmpList);
    }
  }, [data]);

  useEffect(() => {
    if (permission) {
      const global_trusted_list_edit = permission.global_trusted_list_edit;
      const global_trusted_list_view = permission.global_trusted_list_view;
      setPermissions({
        global_trusted_list_edit,
        global_trusted_list_view,
      });
    }
  }, [permission]);

  const watchAddToTrustedMutation = useMutation(addToTrustedList, {
    onSuccess: (res) => {
      if (res?.status == "error") {
        toast({
          title: "Error",
          description: (
            <div>
              Something went wrong!
              <br />
              {typeof res.errors == "string"
                ? res.errors
                : JSON.stringify(res.errors)}
            </div>
          ),
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Added to trusted list",
          variant: "success",
        });
      }
      loadTrustedData();
      refetchGlobalTrustedList();

      setUntrustedRecord((rec: any) =>
        rec.map((r: any) => {
          return {
            ...r,
            checked: false,
            global: false,
            isDatalist: false,
            partial: r.partial == "-1" ? "-1" : "0",
            in_list: "",
            list_value: "",
          };
        }),
      );
      settrustComments({});
      settrustedValues({});
      setInListParameters({});
      settrustedLoader(false);
    },
    onError: () => {
      settrustedLoader(false);
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  const watchDeleteTrustedMutation = useMutation(deleteEntry, {
    onSuccess: () => {
      settrustedLoader(false);
      toast({
        title: "Success",
        description: "Entry Deleted",
        variant: "success",
      });
      loadTrustedData();
    },
    onError: () => {
      settrustedLoader(false);
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  function loadTrustedData() {
    trustedRefetch();
  }

  function remoteTruestedItem(itemId: number) {
    const confirmdelete = confirm("Are you sure you want to remove this item?");
    if (confirmdelete) {
      settrustedLoader(true);
      watchDeleteTrustedMutation.mutate({
        type: "whitelistAnomalySettings",
        epoch: data.anomaly_id + "___" + itemId,
      });
    }
  }

  const [trustComments, settrustComments] = useState<{ [key: string]: string }>(
    {},
  );
  const [trustedValues, settrustedValues] = useState<{ [key: string]: string }>(
    {},
  );

  function handleTrustChange(e: boolean, key: string) {
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key ? { ...item, checked: e } : item,
      ),
    );
  }

  function handleGlobalTrustChange(e: boolean, key: string) {
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key ? { ...item, global: e } : item,
      ),
    );
  }

  function handleIsDatalistChange(e: boolean, key: string) {
    const checked = e === true;
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key
          ? {
              ...item,
              isDatalist: checked,
              in_list: checked ? item.in_list : "",
              list_value: checked ? item.list_value : "",
            }
          : item,
      ),
    );

    if (!checked) {
      setInListParameters((prev: any) => ({
        ...prev,
        [key]: [],
      }));
    }
  }

  function handlePartialChange(e: boolean, key: string) {
    const checked = e === true;
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key
          ? {
              ...item,
              partial: checked ? "1" : "0",
              isDatalist: checked ? false : item.isDatalist,
              in_list: checked ? "" : item.in_list,
              list_value: checked ? "" : item.list_value,
            }
          : item,
      ),
    );

    if (checked) {
      setInListParameters((prev: any) => ({
        ...prev,
        [key]: [],
      }));
    }
  }

  function handleTrustComments(e: string, key: string) {
    const old: { [key: string]: string } = trustComments;
    old[key] = e;
    settrustComments(old);
  }

  function handleTrustValue(e: any, key: string) {
    if (e.includes(",")) {
      toast({
        title: "Error",
        description: "Comma-separated values are not allowed",
        variant: "destructive",
      });
      return;
    }
    const old: { [key: string]: string } = trustedValues;
    old[key] = e;
    settrustedValues(old);
  }

  function handleInListChange(e: string, key: string) {
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key ? { ...item, in_list: e } : item,
      ),
    );
    const parsedData = datalistdata.find((item: any) => item.list_name === e);
    if (parsedData && "parameters" in parsedData) {
      const columns = parsedData.parameters.columns;
      setInListParameters({
        ...inListParameters,
        [key]: columns,
      });
      return;
    }
  }

  function handleInListParameterChange(e: string, key?: string) {
    setUntrustedRecord((prev: any) =>
      prev.map((item: any) =>
        item.key == key ? { ...item, list_value: e } : item,
      ),
    );
  }

  const [untrustedTableRows, setUntrustedTableRows] = useState<any>([]);
  const hasDatalistEnabled = untrustedRecord.some(
    (item: any) => !!item.isDatalist,
  );
  const untrustedTableColumns = [
    { header: "Trusted List", accessor: "trusted", type: "component" },
    ...(permissions.global_trusted_list_edit === 1
      ? [
          {
            header: "Global Trusted List",
            accessor: "global",
            type: "component",
          },
        ]
      : []),
    { header: "Type", accessor: "type", type: "component" },
    { header: "Partial Match", accessor: "partial", type: "component" },
    { header: "Value", accessor: "value", type: "component" },
    { header: "Is Datalist", accessor: "isDatalist", type: "component" },
    ...(hasDatalistEnabled
      ? [
          { header: "In List", accessor: "in_list", type: "component" },
          {
            header: "List Value",
            accessor: "list_value",
            type: "component",
          },
        ]
      : []),
    { header: "Comment", accessor: "comment", type: "component" },
  ];

  const trustValues: any = {
    sip: "",
    sport: "",
    smac: "",
    shost: "",
    dhost: "",
    dip: "",
    dport: "",
    source_subnet: "",
    destination_subnet: "",
  };

  const keyNamePair: any = {
    sip: "Source IP",
    sport: "Source Port",
    smac: "Source MAC",
    shost: "Source Hostname",
    dhost: "Destination Hostname",
    dip: " Destination IP",
    dport: "Destination Port",
    subnet: "Subnet",
    source_subnet: "Source Subnet",
    destination_subnet: "Destination Subnet",
  };

  const exludeFromDisplay = [
    "Source IP",
    "sip",
    "Source Port",
    "sport",
    "Destination IP",
    "dip",
    "Source MAC",
    "smac",
    "Source Port",
    "sport",
    "Destination Port",
    "dport",
  ];

  React.useEffect(() => {
    if (data && data["eventData"]) {
      const eventData = data["eventData"];
      eventData.forEach((element: any) => {
        const edata = JSON.parse(element.EventData);

        let source_subnet = "";
        const sip_split = edata["sip"]?.split(".") || [];
        if (sip_split.length > 2) {
          source_subnet =
            sip_split[0] + "." + sip_split[1] + "." + sip_split[2] + ".0/24";
        }

        let ip_subnet = "";
        if (sip_split.length == 0 && edata["IP"]) {
          const ip_split = edata["IP"]?.split(".");
          ip_subnet =
            ip_split[0] + "." + ip_split[1] + "." + ip_split[2] + ".0/24";
        }

        let destination_subnet = "";
        const dip_split = edata["dip"]?.split(".") || [];
        if (dip_split.length > 2) {
          destination_subnet =
            dip_split[0] + "." + dip_split[1] + "." + dip_split[2] + ".0/24";
        }

        //New process
        const staticKeys: any = [];
        const temptrusted: { [key: string]: string } = {};
        if (edata["sip"]) {
          trustValues["sip"] = edata["sip"];
          staticKeys.push({
            name: "Source IP",
            key: "sip",
            value: edata["sip"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata["sport"]) {
          trustValues["sport"] = edata["sport"];
          staticKeys.push({
            name: "Source Port",
            key: "sport",
            value: edata["sport"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata["smac"]) {
          trustValues["smac"] = edata["smac"];
          staticKeys.push({
            name: "Source MAC",
            key: "smac",
            value: edata["smac"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata.data?.["shost"]) {
          trustValues["shost"] = edata.data?.["shost"];
          staticKeys.push({
            name: "Source Hostname",
            key: "shost",
            value: edata.data?.["shost"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata.data?.["dhost"]) {
          trustValues["dhost"] = edata.data?.["dhost"];
          staticKeys.push({
            name: "Destination Hostname",
            key: "dhost",
            value: edata.data?.["dhost"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata["dip"]) {
          trustValues["dip"] = edata["dip"];
          staticKeys.push({
            name: "Destination IP",
            key: "dip",
            value: edata["dip"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (edata["dport"]) {
          trustValues["dport"] = edata["dport"];
          staticKeys.push({
            name: "Destination Port",
            key: "dport",
            value: edata["dport"],
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
        }

        if (source_subnet != "") {
          trustValues["source_subnet"] = source_subnet;
          staticKeys.push({
            name: "Source Subnet",
            key: "source_subnet",
            value: source_subnet,
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
          temptrusted["source_subnet"] = source_subnet;
        }

        if (destination_subnet != "") {
          trustValues["destination_subnet"] = destination_subnet;
          staticKeys.push({
            name: "Destination Subnet",
            key: "destination_subnet",
            value: destination_subnet,
            checked: false,
            global: false,
            partial: "-1",
            comment: "",
          });
          temptrusted["destination_subnet"] = destination_subnet;
        }

        if (ip_subnet != "") {
          trustValues["subnet"] = ip_subnet;
          staticKeys.push({
            name: "Subnet",
            key: "subnet",
            value: ip_subnet,
            checked: false,
            global: false,
            partial: "0",
            comment: "",
          });
          temptrusted["subnet"] = ip_subnet;
        }

        // Display fields
        Object.keys(edata["display"]).forEach((key: string) => {
          if (!exludeFromDisplay.includes(key)) {
            trustValues[key] = edata["display"][key];
            keyNamePair[key] = key;

            //Hiding the Scanned IPs field for anomaly_id 75178 (Network scan) as per case #4630
            if (
              data["anomaly_id"] == 75178 &&
              (key == "Scanned IPs" || key == "Type of Scan")
            )
              return;

            //Hiding the Scanned Ports field for anomaly_id 75177 (Port scan) as per case #4631
            if (
              data["anomaly_id"] == 75177 &&
              (key == "Scanned Ports" || key == "Type of Scan")
            )
              return;

            staticKeys.push({
              name: key,
              key: key,
              value: edata["display"][key],
              checked: false,
              global: false,
              partial: "0",
              comment: "",
              type: "display",
            });
            temptrusted[key] = edata["display"][key];
          }
        });

        settrustedValues(temptrusted);
        setUntrustedRecord(staticKeys);
      });
    }
  }, [data]);

  const { data: datalistdata, isLoading: datalistLoading } = useQuery(
    ["fetchDataListData"],
    ({ signal }) => fetchDataListData(signal),
  );

  React.useEffect(() => {
    if (untrustedRecord && trustedData) {
      const temp: any = [];
      untrustedRecord.forEach((row: any) => {
        const rowObj: any = {
          trusted: (
            <div>
              <Checkbox
                className="ml-10"
                checked={row.checked}
                onCheckedChange={(e: any) => handleTrustChange(e, row.key)}
              />
            </div>
          ),

          global:
            permissions.global_trusted_list_edit === 1 ? (
              <div>
                <Checkbox
                  className="ml-10"
                  checked={row.global}
                  onCheckedChange={(e: any) =>
                    handleGlobalTrustChange(e, row.key)
                  }
                />
              </div>
            ) : (
              ""
            ),

          type: row.name,

          value: (
            <div className="max-w-[300px] break-all">
              {row.partial == "1" ? (
                <Input
                  defaultValue={
                    row.key in trustedValues
                      ? trustedValues[row.key]
                      : row.value
                  }
                  onChange={(e) => handleTrustValue(e.target.value, row.key)}
                  className={`text-xs ${row.partial == "1" ? "" : "px-0 border-none pointer-events-none"}`}
                />
              ) : (
                row.value
              )}
            </div>
          ),

          isDatalist: (
            <div>
              <Checkbox
                className="ml-10"
                checked={!!row.isDatalist}
                onCheckedChange={(e: any) => handleIsDatalistChange(e, row.key)}
                disabled={row.partial == "1"}
              />
            </div>
          ),

          in_list: row.isDatalist ? (
            <div className="max-w-[300px] break-all">
              <CustomSelect
                data={datalistdata?.map((item: any) => ({
                  ...item,
                  label: item.list_title.trim(),
                  value: item.list_name.trim(),
                }))}
                defaultValue={row.in_list}
                clearOnSelect={true}
                placeholder="Select a Value..."
                onSelect={(e: any) => handleInListChange(e, row.key)}
                disabled={row.partial == "1"}
              />
            </div>
          ) : (
            ""
          ),

          list_value: row.isDatalist ? (
            <div className="max-w-[300px] break-all">
              <CustomSelect
                data={
                  inListParameters[row.key]?.map((item: any) => ({
                    ...item,
                    label: item.column_label || item.column_name,
                    value: item.column_name + "___" + item.column_type,
                  })) || []
                }
                defaultValue={row.list_value}
                clearOnSelect={true}
                placeholder="Select a Value..."
                onSelect={(e: any) => handleInListParameterChange(e, row.key)}
                disabled={row.partial == "1"}
              />
            </div>
          ) : (
            ""
          ),

          partial:
            row.partial != "-1" ? (
              <Checkbox
                className="ml-10"
                checked={row.partial == "1"}
                onCheckedChange={(e: any) => handlePartialChange(e, row.key)}
                disabled={!!row.in_list || !!row.list_value || !!row.isDatalist}
              />
            ) : (
              ""
            ),

          comment: (
            <Input
              defaultValue={trustComments[row.key] || ""}
              onChange={(e) => handleTrustComments(e.target.value, row.key)}
            />
          ),
        };

        temp.push(rowObj);
      });

      setUntrustedTableRows(temp);

      const tmdata: any = [];
      const matchedRows: any = [];
      trustedData.forEach((tdata: any) => {
        // const matched = true;

        // if (!matched) return;

        const gTrustedMatch = gTrustedList?.data?.find((g: any) => {
          return String(g.id) == String(tdata.id);
        });

        const isGlobal = gTrustedMatch?.is_global == true;

        // let matched = true;
        // untrustedRecord.forEach((urec: any) => {
        //   tdata.filters.forEach((filter: any) => {
        //     if (filter.label == urec.key) {
        //       if (filter.value != urec.value) {
        //         matched = false;
        //       }
        //     }
        //   });
        // });

        // if (!matched) {
        //   return;
        // }
        matchedRows.push(tdata);

        tmdata.push(
          <tr className="border-b-2" key={tdata.id}>
            <td className="p-4 border-r">{tdata.operator}</td>
            <td>
              {tdata.filters.map((filter: any, index: number) => {
                let label = filter.label;
                untrustedRecord.forEach((f: any) => {
                  if (f.key == filter.label) {
                    label = f.name || filter.label;
                  }
                });

                return (
                  <div
                    key={index}
                    className="flex flex-col border-b last:border-none"
                  >
                    <span className="p-4">{label}</span>
                  </div>
                );
              })}
            </td>
            <td>
              {tdata.filters.map((filter: any, index: number) => (
                <div
                  key={index}
                  className="flex flex-col border-b last:border-none"
                >
                  <span className="p-4 flex items-center gap-1 relative">
                    {filter.value}
                    {isGlobal && (
                      <Crown
                        className="text-yellow-500 absolute right-0"
                        size={16}
                      />
                    )}
                  </span>
                </div>
              ))}
            </td>
            <td>
              {tdata.filters.map((filter: any, index: number) => (
                <div
                  key={index}
                  className="flex flex-col border-b last:border-none"
                >
                  <span className="p-4 flex items-center gap-1 relative">
                    {filter?.advanced?.args?.listName}
                  </span>
                </div>
              ))}
            </td>
            <td>
              {tdata.filters.map((filter: any, index: number) => (
                <div
                  key={index}
                  className="flex flex-col border-b last:border-none"
                >
                  <span className="p-4 flex items-center gap-1 relative">
                    {
                      datalistdata
                        ?.find(
                          (item: any) =>
                            item.list_name === filter?.advanced?.args?.listName,
                        )
                        ?.parameters?.columns?.filter(
                          (col: any) =>
                            col.column_name ===
                              filter?.advanced?.args?.value[0]?.split(
                                "___",
                              )[0] &&
                            col.column_type ===
                              filter?.advanced?.args?.value[0]?.split("___")[1],
                        )[0]?.column_label
                    }
                  </span>
                </div>
              ))}
            </td>
            <td>
              {tdata.filters.map((filter: any, index: number) => (
                <div
                  key={index}
                  className="flex flex-col border-b last:border-none"
                >
                  <span className="p-4">
                    {filter.partial_match == 1 ? "Yes" : "No"}
                  </span>
                </div>
              ))}
            </td>
            <td>
              {tdata.filters.map((filter: any, index: number) => (
                <div
                  key={index}
                  className="flex flex-col border-b last:border-none"
                >
                  <span className="p-4">{filter.comment}</span>
                </div>
              ))}
            </td>
            <td className="border-l">
              <div className="flex flex-col items-center">
                <Trash2
                  size={14}
                  onClick={() => remoteTruestedItem(tdata.id)}
                  className="cursor-pointer"
                />
              </div>
            </td>
          </tr>,
        );
      });

      setMatchedTrustedItems(matchedRows);
      settrustedTableRows(tmdata);
    }
  }, [untrustedRecord, trustedData, gTrustedList]);

  function validateFields() {
    let valid = true;

    // const matched: boolean[] = [];
    // untrustedRecord.forEach((urec: any) => {
    //   if (urec.checked) {
    //     const _temp = matchedTrustedItems.find((item: any) => {
    //       return item.filters.some(
    //         (filter: any) =>
    //           filter.value == urec.value && filter.label == urec.key,
    //       );
    //     });
    //     if (_temp) {
    //       matched.push(true);
    //     }
    //   }
    // });
    let matchFound = false;
    matchedTrustedItems.forEach((item: any) => {
      const filters = item.filters;
      let matchCount = 0;

      const checkcount = untrustedRecord.filter(
        (urec: any) => urec.checked,
      ).length;

      untrustedRecord.forEach((urec: any) => {
        if (urec.checked) {
          const _temp = filters.find(
            (filter: any) =>
              filter.value == urec.value && filter.label == urec.key,
          );
          if (_temp) {
            matchCount++;
          }
        }
      });

      if (matchCount == filters.length && filters.length == checkcount) {
        matchFound = true;
      }
    });

    if (matchFound) {
      toast({
        title: "Warning",
        description: "This item is already in trusted list",
        variant: "destructive",
      });
      valid = false;
      return;
    }
    for (const item of untrustedRecord) {
      if (item.checked || item.global) {
        const fieldName = keyNamePair[item.key] || item.key;
        if (!trustComments[item.key] || trustComments[item.key] == "") {
          toast({
            title: "Warning",
            description: "Please enter a comment for " + fieldName,
            variant: "destructive",
          });
          valid = false;
          break;
        } else if (item.partial == "1" && !trustedValues[item.key]) {
          toast({
            title: "Warning",
            description: "Please enter a value for " + fieldName,
            variant: "destructive",
          });
          valid = false;
          break;
        } else if (
          item.key == "source_subnet" &&
          !isValidSubnetMask(item.value) &&
          item.partial == "1"
        ) {
          toast({
            title: "Warning",
            description: "Please enter a valid subnet for " + fieldName,
            variant: "destructive",
          });
          valid = false;
          break;
        }
      }
    }
    return valid;
  }

  const trustedList: any = [];
  function submitAddToTrusted(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    e.preventDefault();
    e.stopPropagation();

    const selected = untrustedRecord.filter((item: any) => item.checked);
    if (selected.length == 0) {
      toast({
        title: "Warning",
        description: "Please select atleast one item to add to trusted list",
        variant: "destructive",
      });
      return;
    }

    const fields = ["source_subnet", "destination_subnet"];

    for (const field of fields) {
      if (trustedValues[field] && trustedValues[field].includes(",")) {
        toast({
          title: "Error",
          description: "Please Enter a One Value",
          variant: "destructive",
        });
        return false;
      }
    }
    const valid = validateFields();
    if (!valid) {
      return;
    }

    for (const item of untrustedRecord) {
      if (item.checked || item.global) {
        if (!trustComments[item.key] || trustComments[item.key] == "") {
          toast({
            title: "Warning",
            description: "Please enter a comment for " + keyNamePair[item.key],
            variant: "destructive",
          });
          settrustedLoader(false);
          return;
        }
      }
      trustedList.push({
        field: item.key,
        value:
          trustedValues[item.key] && item.partial == "1"
            ? trustedValues[item.key]
            : item.value,
        listName: item.isDatalist ? item.in_list || "" : "",
        listValue: item.isDatalist ? [item.list_value] : [""],
        isTrusted: item.checked ? "1" : "0",
        isGlobalWhitelist: item.global ? "1" : "0",
        isPartialMatch: item.partial == "1" ? "1" : "0",
        comment: trustComments[item.key] || "",
        EventId: selectedEvent,
      });
    }

    const fixAllAnomalies = fixAllAnomaliesRef?.current?.ariaChecked == "true";
    const expiryDate = expiryDateRef?.current?.value;
    const isDatalist = untrustedRecord.some(
      (item: any) => (item.checked || item.global) && !!item.isDatalist,
    );
    settrustedLoader(true);
    watchAddToTrustedMutation.mutate({
      is_datalist: isDatalist,
      anomalyId: data.anomaly_id,
      operator: operatorType,
      trustedList: JSON.stringify(trustedList),
      fixAllAnomalies,
      expiry: expiryDate
        ? Math.floor(new Date(expiryDate).getTime() / 1000)
        : undefined,
    });
  }

  return (
    <>
      <Dialog open={show} onOpenChange={onClose} modal={false}>
        <div
          className={
            "w-full h-full bg-modalBackground top-0 left-0 z-50 " +
            (show ? "fixed" : "hidden")
          }
        ></div>
        <DialogContent
          className="w-[80vw] max-w-[80vw] max-h-[90vh] overflow-hidden"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Add to Trusted List</DialogTitle>
          </DialogHeader>
          <div className="w-full relative overflow-x-auto px-2">
            <Tabs
              defaultValue="untrusted"
              value={activeTab}
              onValueChange={(val) => {
                setActiveTab(val);
                if (val === "trusted") {
                  refetchGlobalTrustedList();
                  trustedRefetch();
                }
              }}
              className="w-full"
            >
              <TabsList className="grid w-[300px] grid-cols-2">
                <TabsTrigger value="untrusted">Add to Trusted List</TabsTrigger>
                <TabsTrigger value="trusted">Trusted List</TabsTrigger>
              </TabsList>
              <TabsContent
                value="untrusted"
                className="max-h-[75vh] overflow-y-scroll"
              >
                <Select
                  defaultValue={selectedEvent}
                  onValueChange={setSelectedEvent}
                >
                  <SelectTrigger className="w-[35%] m-5 ml-0 ">
                    <SelectValue placeholder="Select an Event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Events</SelectLabel>
                      {eventList.map((evt) => (
                        <SelectItem key={evt.value} value={evt.value}>
                          {evt.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {_trustedLoading || trustedRefetching || datalistLoading ? (
                  <TableLoader colCount={8} />
                ) : (
                  <div className="overflow-x-auto px-2">
                    <DataTable
                      // pagination={false}
                      search={false}
                      colFilter={false}
                      sort={false}
                      cols={untrustedTableColumns}
                      data={untrustedTableRows}
                      className="min-w-[1100px] [&_td]:p-2"
                    />
                  </div>
                )}
                <div className="flex justify-between pl-1 pr-2 items-center">
                  <div className="mt-2">
                    <span className="inline-block font-bold mr-3">
                      Operator
                    </span>
                    <Tabs defaultValue="AND" className="w-[120px] inline-block">
                      <TabsList className="grid grid-cols-2">
                        <TabsTrigger
                          onClick={() => setoperatorType("AND")}
                          value="AND"
                        >
                          AND
                        </TabsTrigger>
                        <TabsTrigger
                          onClick={() => setoperatorType("OR")}
                          value="OR"
                        >
                          OR
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  <div className="mt-2">
                    <span className="inline-block font-bold mr-3">
                      Expiry Date
                    </span>
                    <div className="w-[200px] inline-block">
                      <Input
                        id="toTime"
                        type="datetime-local"
                        onClick={(e: any) => showInputPicker(e)}
                        ref={expiryDateRef}
                        defaultValue=""
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 py-2 flex items-center">
                  <Checkbox
                    className="mr-3"
                    id="fixAllAnomalies"
                    ref={fixAllAnomaliesRef}
                  />
                  <label
                    htmlFor="fixAllAnomalies"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Fix all anomalies
                  </label>
                </div>

                <div className="text-left w-full mt-4">
                  {trustedLoader ? (
                    <Button type="submit">
                      <Loader className="animate-spin" />
                    </Button>
                  ) : (
                    <Button type="submit" onClick={submitAddToTrusted}>
                      Submit
                    </Button>
                  )}
                </div>
              </TabsContent>
              <TabsContent
                value="trusted"
                className="max-h-[70vh] overflow-y-scroll mt-4"
              >
                {_trustedLoading ||
                trustedRefetching ||
                datalistLoading ||
                permissions.global_trusted_list_view == 0 ? (
                  <TableLoader colCount={8} />
                ) : (
                  <div className="border rounded-md overflow-x-auto">
                    <table className="min-w-[1100px] text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="p-4">Operator</th>
                          <th className="p-4">Type</th>
                          <th className="p-4">Value</th>
                          <th className="p-4">In List</th>
                          <th className="p-4">List Value</th>
                          <th className="p-4">Partial Match</th>
                          <th className="p-4">Comment</th>
                          <th className="p-4 text-center">Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trustedTableRows.map((tdata: any) => {
                          return tdata;
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrustedListDialog;
